// OfferFilter interfaces
import { LocationKind } from '@/_store/current-offer/current-offer.types';
import { ProductKind, TransportKind } from '@/offer/offer.model';

export interface BaseFilter {
	id: number;
	name: string;
}

export interface BaseFilterStop extends BaseFilter {
	kind: TransportKind;
}

export interface OfferDateRange {
	upper: string;
	lower: string;
}

export interface OfferFilter {
	boards: BaseFilter[];
	nights: { min: number[]; max: number[] };
	defaultNights: number[];
	adults: number[];
	children: number[];
	stops: BaseFilterStop[];
	products: BaseFilter[];
	locations: BaseFilter[];
	kinds: BaseFilter[];
	conveniences: BaseFilter[];
	count: number;
	offersDateRange: OfferDateRange;
	search: string;
	transportKind: TransportKind[];
	price: { min: number; max: number };
}

export interface ApiOfferFilter extends Omit<OfferFilter, 'nights' | 'defaultNights'> {
	nights: number[];
}

export interface Selected {
	boards: number[];
	nights: Nights;
	adults: number;
	children: number;
	roomType: number[];
	stops: number[];
	locations: number[];
	kinds: number[];
	conveniences: number[];
	tags: number[];
	offersDateRange: OfferDateRange;
	search: string;
	status: number;
	highlightLastSeats: boolean;
	transportKind: TransportKind[];
	products: number[];
	price: { min: number; max: number };
}

export interface Nights {
	min: number;
	max: number;
}

// Offer interfaces
export interface OfferList {
	product: Product;
	board: Board;
	term: Term;
	dateRange: DateRange;
	catalogPrice: number;
	fullPrice: number;
	locations: Location[];
	roomType: RoomType;
	highlightLastSeats: boolean;
	promotion: Promotion;
	multimedia: MultimediaWithOrder[];
	conveniences: Convenience[];
	stop: Stop;
	transport: { kind: TransportKind; transportDeparture: number; transportReturn: number };
}

export interface Stop {
	departureDatetime: string;
	id: number;
	returnDatetime: string;
}

export interface Convenience {
	convenience: string;
	id: number;
	multimedia: MultimediaWithOrder[];
	name: string;
}

export interface Catalog {
	id: number;
	name: string;
	multimedia: string;
}

export interface Promotion {
	id: number;
	name: string;
	catalog: Catalog;
	description: string;
	method: string;
	methodValue: number;
}

export interface Location {
	id: number;
	name: string;
	kind: LocationKind;
	parentLocation: Location | null;
}

export interface MultimediaWithOrder {
	id: number;
	multimedia: Multimedia;
	order: number;
}

export interface Multimedia {
	id: string;
	name: string;
	url: string;
}

export interface DateRange {
	lower: string;
	upper: string;
	bounds: string;
}

export interface Term {
	id: number;
	nightStay: number;
	status: number;
}

export interface Board {
	id: number;
	name: string;
}

export interface Product {
	id: number;
	name: string;
	kind: ProductKind;
}

export interface RoomType {
	id: number;
	adults: number;
	children: number;
}

export interface NumericDictionary<T> {
	[key: number]: T;
}

export interface OfferListParams {
	adults: number;
	children: number;
	boards: number[];
	conveniences: number[];
	kinds: number[];
	locations: number[];
	stops: number[];
	tags: number[];
	nights_min: number;
	nights_max: number;
	date_range: ParamsDateRange;
	search: string;
	status: number;
	highlight_last_seats: boolean;
	transport_kind: TransportKind[];
	products: number[];
	price_min: number;
	price_max: number;
}

export type ParamsDateRange = `${string},${string}` | '';

export interface OfferListHeaderOption {
	id: string | number;
	name: string;
}

export interface OfferListHeader {
	key: string;
	name: string;
	value: string | number;
	defaultOption: boolean;
	options?: OfferListHeaderOption[];
}

export type OfferListHeaderClearEvent = OfferListHeader & {
	removedOptionId?: string | number;
};

export const SORTING_OPTIONS = {
	'-popularity_score,departure_date_pl_tz': 'Najpopularniejsze',
	'-full_price,departure_date_pl_tz': 'Cena malejąco',
	'full_price,departure_date_pl_tz': 'Cena rosnąco',
	'departure_date_pl_tz,full_price': 'Data od najwcześniejszej',
	'-departure_date_pl_tz,full_price': 'Data od najpóźniejszej',
};

export type SortingOptions = keyof typeof SORTING_OPTIONS;

export const DEFAULT_SORTING: keyof typeof SORTING_OPTIONS = '-popularity_score,departure_date_pl_tz';

export const DEFAULT_PAGINATION_OFFSET = 0;

export const DEFAULT_PAGINATION_LIMIT = 10;
